// src/components/calendar/BlockTimePanel/BlockTimePanel.jsx
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../../utils/cn';

const formatDateStr = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatHour = (hour) => {
  const h = hour % 12 || 12;
  const ampm = hour < 12 ? 'AM' : 'PM';
  return `${h}:00 ${ampm}`;
};

// Groups slots into human-readable labels for display
const groupSlotsForDisplay = (slots) => {
  const allDay = slots.filter(s => s.allDay);
  const hourly = slots.filter(s => !s.allDay);
  const labels = [];

  allDay.forEach(s => {
    labels.push(`${formatDateStr(s.dateStr)} — All day`);
  });

  const sorted = [...hourly].sort((a, b) => {
    if (a.dateStr < b.dateStr) return -1;
    if (a.dateStr > b.dateStr) return 1;
    return a.hour - b.hour;
  });

  let i = 0;
  while (i < sorted.length) {
    const dateStr = sorted[i].dateStr;
    const startHour = sorted[i].hour;
    let endHour = startHour + 1;
    while (
      i + 1 < sorted.length &&
      sorted[i + 1].dateStr === dateStr &&
      sorted[i + 1].hour === sorted[i].hour + 1
    ) {
      i++;
      endHour = sorted[i].hour + 1;
    }
    labels.push(`${formatDateStr(dateStr)}, ${formatHour(startHour)} – ${formatHour(endHour)}`);
    i++;
  }

  return labels;
};

const BlockTimePanel = ({ slots = [], onSubmit, onClose }) => {
  const [title, setTitle] = useState('');
  const displayLabels = groupSlotsForDisplay(slots);

  const handleSubmit = () => {
    onSubmit(title.trim() || 'Unavailable');
  };

  return (
    <div className="flex flex-col h-full bg-slate-900/95 backdrop-blur-xl">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50">
        <h2 className="text-lg font-semibold text-slate-100">Block Time</h2>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
        <div>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">
            Selected Times
          </p>
          <div className="space-y-1.5">
            {displayLabels.map((label, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg"
              >
                <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0" />
                <span className="text-sm text-slate-200">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-2">
            Label (optional)
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Unavailable"
            className="w-full px-3 py-2 bg-slate-800/60 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500/50 focus:bg-slate-800/80 transition-colors"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-slate-700/50 flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2 border border-slate-600/50 text-slate-300 rounded-lg text-sm hover:bg-slate-700/30 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={slots.length === 0}
          className={cn(
            'flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            slots.length > 0
              ? 'bg-blue-600 hover:bg-blue-500 text-white'
              : 'bg-slate-700 text-slate-500 cursor-not-allowed'
          )}
        >
          Block Time
        </button>
      </div>
    </div>
  );
};

export default BlockTimePanel;
